import { Box, Card, Center, Container, Flex, Image, Link, Text } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';

import { AngryGuy } from '../../assets/Images';
import Loader from '../../components/Loader';
import Subtitle from '../../components/Subtitle';
import Title from '../../components/Title';
import TitleContainer from '../../components/TitleContainer';
import { SSNInput } from '../../components/formComponents';
import PrimaryButton from '../../components/ui/buttons/PrimaryButton';
import { isProd } from '../../config';
import { MAIN_NUMBER } from '../../constants/contact';
import {
  ConsentAgreementTypeEnum,
  useCurrentConsentAgreementTextQuery,
  useSecureLinkCustomerUpdateMutation,
  useValidateSecureLinkQuery,
} from '../../gql/generated/graphql';
import hotjar, { HotjarEvents } from '../../services/analytics/hotjar';
import SecureDataEntryDevToolbar from './SecureDataEntryDevToolbar';

const validationSchema = Yup.object({
  ssn: Yup.string().required('SSN is required'),
});

const initialValues = {
  ssn: '',
};

const SecureDataEntry = () => {
  const [submitted, setSubmitted] = useState(false);
  const [isInvalid, setIsInvalid] = useState(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');
  const {
    data: validateSecureLinkData,
    loading: validateSecureLinkLoading,
    error: validateSecureLinkError,
  } = useValidateSecureLinkQuery({
    variables: {
      token: token || '',
    },
  });
  const isSecureLinkValid = validateSecureLinkData?.validateSecureLink;
  const [secureLinkCustomerUpdate] = useSecureLinkCustomerUpdateMutation();
  const { data: consentAgreementData } = useCurrentConsentAgreementTextQuery({
    variables: { agreementType: ConsentAgreementTypeEnum.SecureLinkBuyerSsn },
  });
  const consentText = consentAgreementData?.currentConsentAgreementText?.text;

  useEffect(() => {
    hotjar.track(HotjarEvents.SecureDataEntry);
  }, []);

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      await secureLinkCustomerUpdate({
        variables: {
          ssn: values.ssn,
          token: token || '',
        },
        context: {
          isErrorHandled: true,
        },
      });
      setSubmitted(true);
    } catch (error) {
      setIsInvalid(true);
    }
  };

  if (validateSecureLinkLoading) {
    return <Loader />;
  }

  if (isInvalid || !token || !isSecureLinkValid || validateSecureLinkError) {
    return (
      <Container>
        <Flex justifyContent="center">
          <Image
            mr={20}
            src={AngryGuy}
            alt="Angry Guy"
            maxWidth="150px"
            display={{ base: 'none', md: 'block' }}
          />
          <Box fontSize="16px" textAlign={{ base: 'center', md: 'left' }}>
            <TitleContainer justifyContent="flex-start">
              <Title>This link is no longer valid</Title>
            </TitleContainer>
            <Subtitle>Reach out to us if you feel you reached this page in error.</Subtitle>
            <Link href={`tel:+1-${MAIN_NUMBER}`}>{MAIN_NUMBER}</Link>
          </Box>
        </Flex>
      </Container>
    );
  }

  if (submitted) {
    return (
      <Container textAlign="center">
        <TitleContainer>
          <Title>Submission Received</Title>
        </TitleContainer>
        <Subtitle>Your information has been securely recorded.</Subtitle>
      </Container>
    );
  }

  return (
    <Box>
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {({ isSubmitting }) => (
          <Form>
            <Container textAlign="center" maxW="container.xs">
              <TitleContainer>
                <Title>Secure Data Entry</Title>
              </TitleContainer>
              <Subtitle>A safe and secure way to input your information.</Subtitle>
              <Card borderRadius="15px" p={{ base: '15px', md: '30px' }}>
                <SSNInput name="ssn" digits="9" />
                {consentText && (
                  <Text textAlign="center" color="gray" fontSize="14px" mt={4}>
                    {consentText}
                  </Text>
                )}
                <Center mt={4}>
                  <PrimaryButton type="submit" isLoading={isSubmitting}>
                    SUBMIT
                  </PrimaryButton>
                </Center>
              </Card>
            </Container>
          </Form>
        )}
      </Formik>
      {!isProd && <SecureDataEntryDevToolbar setIsInvalid={setIsInvalid} />}
    </Box>
  );
};

export default SecureDataEntry;
